import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../cx';

/**
 * D-09 §15.11 — 우클릭(컨텍스트) 메뉴 + 하위 메뉴 (`.menu` · `.menu-item` · `.menu-sep` 재사용, `.menu-sub` 추가).
 *
 * 화면 좌표(x, y)에 떠오르는 액션 목록. document.body 에 포탈로 그려 어떤 overflow 조상에도 잘리지 않고,
 * 화면 가장자리에서는 안쪽으로 당긴다. 항목에 `items` 가 있으면 오른쪽에 하위 메뉴가 열린다
 * (마우스를 올리거나 → 키·Enter·클릭, 공간이 모자라면 왼쪽으로 뒤집는다). 쌓임은 `--z-dropdown`.
 * 바깥 클릭·Esc 로 닫힌다. 항목을 고르면(하위 메뉴 항목 포함) 호출부 onSelect 뒤 메뉴가 닫힌다.
 * 키보드: ↑↓ 이동 · → 하위 메뉴 열기 · ← 하위 메뉴 닫기 · Enter/Space 선택 · Esc 닫기.
 */
export interface ContextMenuItem {
  key: string;
  label: React.ReactNode;
  /** 잎 항목을 골랐을 때 */
  onSelect?: () => void;
  /** 있으면 하위 메뉴를 가진 항목(onSelect 는 쓰지 않는다) */
  items?: ContextMenuItem[];
  danger?: boolean;
  disabled?: boolean;
}

export interface ContextMenuProps {
  open: boolean;
  /** 화면(viewport) 좌표 — 우클릭 지점 */
  x: number;
  y: number;
  onClose: () => void;
  /** 접근성 레이블(필수) */
  'aria-label': string;
  /** 목록 위 머리글(예: 대상 이름) */
  heading?: React.ReactNode;
  items: ContextMenuItem[];
  className?: string;
}

const MARGIN = 8;

interface ListProps {
  items: ContextMenuItem[];
  onPick: (item: ContextMenuItem) => void;
  /** 하위 목록이면 닫기(←) 요청 */
  onBack?: () => void;
  autoFocusFirst?: boolean;
  /** 목록 접근성 레이블(맨 위 목록만) */
  label?: string;
}

/** 한 단계 목록 — 하위 메뉴는 항목 안에 재귀로 그린다 */
function MenuList({ items, onPick, onBack, autoFocusFirst, label }: ListProps) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [focusSub, setFocusSub] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const buttons = () =>
    Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>(':scope > .menu-item-wrap > button[role="menuitem"]:not(:disabled)') ?? []);

  useEffect(() => {
    if (autoFocusFirst) requestAnimationFrame(() => buttons()[0]?.focus());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const list = buttons();
    const idx = list.findIndex((b) => b === document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      list[(idx + 1) % list.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      list[(idx - 1 + list.length) % list.length]?.focus();
    } else if (e.key === 'ArrowLeft' && onBack) {
      e.preventDefault();
      e.stopPropagation();
      onBack();
    }
  };

  return (
    <div ref={listRef} role="menu" aria-label={label} onKeyDown={onKeyDown}>
      {items.map((it) => {
        const hasSub = !!it.items && it.items.length > 0;
        const subOpen = hasSub && openKey === it.key && !it.disabled;
        return (
          <div
            key={it.key}
            className="menu-item-wrap"
            onMouseEnter={() => { setOpenKey(hasSub ? it.key : null); setFocusSub(false); }}
          >
            <button
              type="button"
              role="menuitem"
              disabled={it.disabled}
              aria-haspopup={hasSub ? 'menu' : undefined}
              aria-expanded={hasSub ? subOpen : undefined}
              className={cx('menu-item', it.danger && 'danger', subOpen && 'active')}
              onClick={() => {
                if (hasSub) { setOpenKey(it.key); setFocusSub(true); return; }
                onPick(it);
              }}
              onKeyDown={(e) => {
                if (hasSub && e.key === 'ArrowRight') {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpenKey(it.key);
                  setFocusSub(true);
                }
              }}
            >
              <span className="menu-label">{it.label}</span>
              {hasSub && <span className="menu-caret" aria-hidden="true">›</span>}
            </button>
            {subOpen && (
              <SubMenu
                items={it.items as ContextMenuItem[]}
                onPick={onPick}
                focusFirst={focusSub}
                onBack={() => {
                  setOpenKey(null);
                  requestAnimationFrame(() => {
                    listRef.current
                      ?.querySelector<HTMLButtonElement>(`:scope > .menu-item-wrap > button[aria-expanded]`)
                      ?.focus();
                  });
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function SubMenu({ items, onPick, onBack, focusFirst }: { items: ContextMenuItem[]; onPick: (i: ContextMenuItem) => void; onBack: () => void; focusFirst: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<React.CSSProperties>({ visibility: 'hidden' });

  // 오른쪽 공간이 모자라면 왼쪽으로, 아래가 모자라면 위로 당긴다
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const style: React.CSSProperties = {};
    if (r.right > window.innerWidth - MARGIN) { style.left = 'auto'; style.right = 'calc(100% - var(--spacing-02))'; }
    if (r.bottom > window.innerHeight - MARGIN) {
      // 아래가 모자라면 넘친 만큼 위로 올린다(기본 위치 = 항목 머리보다 spacing-03 위)
      const over = Math.ceil(r.bottom - (window.innerHeight - MARGIN));
      style.top = `calc(var(--spacing-03) * -1 - ${over}px)`;
    }
    setPlace(style);
  }, [items.length]);

  return (
    <div ref={ref} className="menu menu-sub show" style={place}>
      <MenuList items={items} onPick={onPick} onBack={onBack} autoFocusFirst={focusFirst} />
    </div>
  );
}

export function ContextMenu({ open, x, y, onClose, 'aria-label': ariaLabel, heading, items, className = '' }: ContextMenuProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: x, top: y });

  // 화면 밖으로 나가면 안쪽으로 당긴다 — 먼저 지정 좌표에 놓고 크기를 잰 뒤 보정
  useLayoutEffect(() => {
    if (!open) return;
    const el = rootRef.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    setPos({
      left: Math.max(MARGIN, Math.min(x, window.innerWidth - w - MARGIN)),
      top: Math.max(MARGIN, Math.min(y, window.innerHeight - h - MARGIN)),
    });
  }, [open, x, y]);

  // 바깥 클릭·Esc 닫기
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const pick = (item: ContextMenuItem) => {
    item.onSelect?.();
    onClose();
  };

  return createPortal(
    <div
      ref={rootRef}
      className={cx('menu menu-ctx show', className)}
      style={{ left: pos.left, top: pos.top }}
      onContextMenu={(e) => e.preventDefault()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {heading != null && <div className="menu-head">{heading}</div>}
      <MenuList items={items} onPick={pick} autoFocusFirst label={ariaLabel} />
    </div>,
    document.body,
  );
}
